import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Calculator, Euro, TrendingUp, TrendingDown, Check, RotateCcw, Loader2 } from "lucide-react";
import { formatCurrency } from "../../lib/utils";
import api from "../../lib/api";
import { toast } from "sonner";

function MarginCard({ label, data, variant }) {
  if (!data) return null;
  const color = variant === "best" ? "text-green-600" : "text-red-500";
  const Icon = variant === "best" ? TrendingUp : TrendingDown;
  return (
    <div className="p-3 rounded-lg bg-background border" data-testid={`extreme-${variant}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
        <Badge variant={data.margin >= 0 ? "secondary" : "destructive"} className="ml-auto text-xs">
          {data.margin_pct}%
        </Badge>
      </div>
      <div className="flex items-baseline gap-2 mb-1">
        <span className="text-sm text-muted-foreground">Coste:</span>
        <span className="font-semibold">{formatCurrency(data.total_cost)}</span>
        <span className="text-sm text-muted-foreground ml-2">Margen:</span>
        <span className={`font-semibold ${color}`}>{formatCurrency(data.margin)}</span>
      </div>
      <div className="flex flex-wrap gap-1 mt-1">
        {data.dishes.map((d, i) => (
          <Badge key={i} variant="outline" className="text-xs font-normal">
            {d.name} ({formatCurrency(d.cost)})
          </Badge>
        ))}
      </div>
    </div>
  );
}

export default function CostSimulator({ menuId, menuPrice, categories, dishes }) {
  const [selectedDishes, setSelectedDishes] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extremes, setExtremes] = useState(null);
  const [loadingExtremes, setLoadingExtremes] = useState(false);

  const dishesByCategory = useMemo(() => {
    const map = {};
    for (const cat of categories) {
      map[cat.category_id] = dishes.filter(d => d.category_id === cat.category_id);
    }
    return map;
  }, [categories, dishes]);

  useEffect(() => {
    if (menuPrice > 0 && dishes.length > 0) {
      setLoadingExtremes(true);
      api.getCostExtremes(menuId)
        .then(data => setExtremes(data))
        .catch(() => {})
        .finally(() => setLoadingExtremes(false));
    }
  }, [menuId, menuPrice, dishes.length]);

  const toggleDish = (categoryId, dishId) => {
    setSelectedDishes(prev => {
      const current = prev[categoryId];
      if (current === dishId) {
        const next = { ...prev };
        delete next[categoryId];
        return next;
      }
      return { ...prev, [categoryId]: dishId };
    });
    setResult(null);
  };

  const selectedIds = Object.values(selectedDishes);

  const handleCalculate = async () => {
    if (selectedIds.length === 0) {
      toast.error("Selecciona al menos un plato por categoría");
      return;
    }
    setLoading(true);
    try {
      const data = await api.simulateMenuCost(menuId, selectedIds);
      setResult(data);
    } catch (err) {
      toast.error(err.message || "Error al calcular");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedDishes({});
    setResult(null);
  };

  return (
    <Card className="border-primary/20 bg-primary/5" data-testid="cost-simulator">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Calculator className="w-5 h-5 text-primary" />
          Simulador de Coste
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Selecciona un plato por categoría para simular el coste de una combinación
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Auto extremes */}
        {menuPrice > 0 && (
          <div className="space-y-2">
            {loadingExtremes ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Calculando extremos...
              </div>
            ) : extremes && (extremes.best || extremes.worst) ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <MarginCard label="Mejor margen" data={extremes.best} variant="best" />
                <MarginCard label="Peor margen" data={extremes.worst} variant="worst" />
              </div>
            ) : null}
          </div>
        )}

        {/* Manual simulator */}
        <div className="border-t pt-4">
          <p className="text-sm font-medium mb-3">Simulación manual</p>
          {categories.map(cat => {
            const catDishes = dishesByCategory[cat.category_id] || [];
            if (catDishes.length === 0) return null;
            return (
              <div key={cat.category_id} className="mb-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  {cat.name}
                </p>
                <div className="flex flex-wrap gap-2">
                  {catDishes.map(dish => {
                    const isSelected = selectedDishes[cat.category_id] === dish.dish_id;
                    return (
                      <button
                        key={dish.dish_id}
                        onClick={() => toggleDish(cat.category_id, dish.dish_id)}
                        data-testid={`sim-dish-${dish.dish_id}`}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                          isSelected
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background border-border hover:border-primary/50"
                        }`}
                      >
                        {isSelected && <Check className="w-3 h-3 inline mr-1" />}
                        {dish.name}
                        <span className="ml-1 opacity-60">({formatCurrency(dish.price)})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div className="flex gap-3 pt-2">
            <Button
              onClick={handleCalculate}
              disabled={loading || selectedIds.length === 0}
              data-testid="sim-calculate-btn"
            >
              <Calculator className="w-4 h-4 mr-2" />
              {loading ? "Calculando..." : "Calcular coste"}
            </Button>
            {selectedIds.length > 0 && (
              <Button variant="outline" onClick={handleReset} data-testid="sim-reset-btn">
                <RotateCcw className="w-4 h-4 mr-2" />
                Limpiar
              </Button>
            )}
          </div>

          {result && (
            <div className="grid grid-cols-3 gap-4 pt-3 mt-3 border-t" data-testid="sim-result">
              <div className="text-center p-3 rounded-lg bg-background">
                <Euro className="w-5 h-5 mx-auto mb-1 text-orange-500" />
                <p className="text-xl font-bold">{formatCurrency(result.total_cost)}</p>
                <p className="text-xs text-muted-foreground">Coste estimado</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-background">
                <TrendingUp className="w-5 h-5 mx-auto mb-1 text-green-500" />
                <p className={`text-xl font-bold ${result.margin >= 0 ? "text-green-600" : "text-red-500"}`}>
                  {formatCurrency(result.margin)}
                </p>
                <p className="text-xs text-muted-foreground">Margen</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-background">
                <Badge variant={result.margin_pct >= 30 ? "default" : result.margin_pct >= 0 ? "secondary" : "destructive"} className="text-lg px-3 py-1">
                  {result.margin_pct}%
                </Badge>
                <p className="text-xs text-muted-foreground mt-1">% Margen</p>
              </div>
              {result.dishes.length > 0 && (
                <div className="col-span-3">
                  <p className="text-xs text-muted-foreground mb-1">Desglose:</p>
                  <div className="flex flex-wrap gap-2">
                    {result.dishes.map(d => (
                      <Badge key={d.dish_id} variant="outline" className="text-xs">
                        {d.name}: {formatCurrency(d.cost)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
